// ─── Store photos en mémoire pure ────────────────────────────────────────────
// Les photos ne vont JAMAIS dans localStorage ni sessionStorage (quota exceeded)
// Ce module singleton survit aux re-renders et rotations tablette
// Il est détruit uniquement si l'onglet est fermé ou rechargé (F5)
//
// Structure photo :
//   {
//     dataUrl:         string  → photo ANNOTÉE  (canvas) → Supabase + PDF
//     originalDataUrl: string  → photo ORIGINALE (fichier natif) → MRC
//   }
//
// Règles d'intégrité :
//   - Un slot n'est stocké QUE si annotatedDataUrl est une string non vide
//   - Les getters retournent des copies profondes pour éviter mutation externe
//   - getAnnotatedPhotosMap() ne retourne que les slots avec dataUrl valide
//   - ✅ v2.0 — verrouillage sur inspectionKey pour éviter le mélange photos/contrats
// ─────────────────────────────────────────────────────────────────────────────

const _store = {
  photos:        {},    // { [slotId]: { dataUrl: string, originalDataUrl: string } }
  departPhotos:  {},    // idem — chargées depuis Supabase au démarrage retour
  _activeKey:    null,  // ✅ v2.0 — inspectionKey actif (ex: "dep-CD25-04567")
}

// ── Helper interne : copie profonde d'un objet photo ─────────────────────────
function clonePhoto(photo) {
  if (!photo) return null
  return { dataUrl: photo.dataUrl ?? null, originalDataUrl: photo.originalDataUrl ?? null }
}

// ── Helper interne : valide qu'une dataUrl est utilisable ────────────────────
function isValidDataUrl(v) {
  return typeof v === 'string' && v.length > 0
}

// ── Export singleton ──────────────────────────────────────────────────────────
export const photoMemory = {

  // ── v2.0 — Verrouillage sur inspection active ─────────────────────────────

  /**
   * Verrouille le store sur une inspection spécifique.
   * Doit être appelé au début de chaque inspection (startInspection).
   * Toute tentative d'écriture avec un key différent sera bloquée.
   */
  setActiveInspection(inspectionKey) {
    if (_store._activeKey && _store._activeKey !== inspectionKey) {
      console.warn(
        `[photoMemory] ⚠️ Changement d'inspection active : ${_store._activeKey} → ${inspectionKey}. ` +
        `Clear automatique des photos en mémoire.`
      )
      this.clear()
    }
    _store._activeKey = inspectionKey
    console.log(`[photoMemory] 🔒 Inspection active : ${inspectionKey}`)
  },

  /**
   * Retourne la clé d'inspection active.
   */
  getActiveKey() {
    return _store._activeKey
  },

  // ── Setters ─────────────────────────────────────────────────────────────────

  // Enregistre une photo prise par l'agent
  // annotatedDataUrl  : résultat du canvas (avec annotations éventuelles)
  // originalDataUrl   : fichier natif (fileToBase64) — envoyé dans MRC
  // ✅ Guard : slot ignoré si annotatedDataUrl invalide
  setPhoto(slotId, annotatedDataUrl, originalDataUrl) {
    if (!isValidDataUrl(annotatedDataUrl)) {
      console.warn(`[photoMemory] setPhoto ignoré — annotatedDataUrl invalide pour: ${slotId}`)
      return
    }
    _store.photos[slotId] = {
      dataUrl:         annotatedDataUrl,
      originalDataUrl: isValidDataUrl(originalDataUrl) ? originalDataUrl : annotatedDataUrl,
    }
    console.log(
      `[photoMemory] setPhoto ${slotId}`,
      `| key: ${_store._activeKey || 'UNLOCKED'}`,
      `| annotée: ${Math.round(annotatedDataUrl.length * 0.75 / 1024)} Ko`,
      `| originale: ${Math.round((_store.photos[slotId].originalDataUrl.length) * 0.75 / 1024)} Ko`,
      `| total: ${Object.keys(_store.photos).length} photos`
    )
  },

  // Enregistre une photo départ (chargée depuis Supabase pour le retour)
  // ✅ Guard : slot ignoré si annotatedDataUrl invalide
  setDepartPhoto(slotId, annotatedDataUrl, originalDataUrl) {
    if (!isValidDataUrl(annotatedDataUrl)) {
      console.warn(`[photoMemory] setDepartPhoto ignoré — annotatedDataUrl invalide pour: ${slotId}`)
      return
    }
    _store.departPhotos[slotId] = {
      dataUrl:         annotatedDataUrl,
      originalDataUrl: isValidDataUrl(originalDataUrl) ? originalDataUrl : annotatedDataUrl,
    }
  },

  // Injecte toutes les photos départ depuis un objet { zone: url | { dataUrl, originalDataUrl } }
  // ✅ Accepte maintenant DEUX formats :
  //   - string URL (depuis Supabase Storage public URL)
  //   - objet { dataUrl, originalDataUrl } (depuis EDL déjà sauvegardé)
  // ✅ Guard v2 — contractIdExpected : empêche le croisement photos si mauvais contrat
  setDepartPhotosFromUrls(photoUrlsMap, contractIdExpected) {
    // Guard de sécurité : vérifier que le contrat actif correspond
    if (contractIdExpected && _store._activeKey) {
      const activeNum = _store._activeKey.replace(/^(dep|retour)-/, '')
      if (activeNum !== String(contractIdExpected)) {
        console.warn(
          `[photoMemory] 🚫 GUARD setDepartPhotosFromUrls bloqué — ` +
          `activeKey="${_store._activeKey}" mais contractId attendu="${contractIdExpected}". ` +
          `Possible navigation rapide ou état incohérent.`
        )
        return
      }
    }
    _store.departPhotos = {}
    const input = photoUrlsMap && typeof photoUrlsMap === 'object' ? photoUrlsMap : {}
    for (const [slotId, value] of Object.entries(input)) {
      if (!value) continue

      if (typeof value === 'string' && value.length > 0) {
        // Format legacy : URL publique Supabase
        _store.departPhotos[slotId] = { dataUrl: value, originalDataUrl: value }

      } else if (typeof value === 'object') {
        // Format dual : { dataUrl, originalDataUrl }
        const dataUrl        = value.dataUrl         ?? null
        const originalDataUrl = value.originalDataUrl ?? dataUrl
        if (isValidDataUrl(dataUrl)) {
          _store.departPhotos[slotId] = { dataUrl, originalDataUrl }
        }
      }
    }
    console.log(`[photoMemory] departPhotos chargées: ${Object.keys(_store.departPhotos).length} zones`)
  },

  // ── Getters ─────────────────────────────────────────────────────────────────

  // ✅ Copies profondes pour éviter mutation externe des objets photo
  getPhotos() {
    const copy = {}
    for (const [id, photo] of Object.entries(_store.photos)) {
      copy[id] = clonePhoto(photo)
    }
    return copy
  },

  getDepartPhotos() {
    const copy = {}
    for (const [id, photo] of Object.entries(_store.departPhotos)) {
      copy[id] = clonePhoto(photo)
    }
    return copy
  },

  getPhotoCount()      { return Object.keys(_store.photos).length },
  getDepartPhotoCount(){ return Object.keys(_store.departPhotos).length },

  // ✅ Retourne une copie profonde du slot (pas de référence partagée)
  getPhoto(slotId) {
    return clonePhoto(_store.photos[slotId]) ?? null
  },

  getDepartPhoto(slotId) {
    return clonePhoto(_store.departPhotos[slotId]) ?? null
  },

  // ✅ Nouveaux helpers booléens — évite !!photoMemory.getPhoto(id) partout
  hasPhoto(slotId)       { return slotId in _store.photos },
  hasDepartPhoto(slotId) { return slotId in _store.departPhotos },

  // Retourne uniquement les dataUrl annotées valides (pour le PDF)
  // ✅ Filtre les entrées null pour ne pas tromper pdfService
  getAnnotatedPhotosMap() {
    const map = {}
    for (const [slotId, photo] of Object.entries(_store.photos)) {
      if (isValidDataUrl(photo.dataUrl)) {
        map[slotId] = photo.dataUrl
      }
    }
    return map
  },

  // Retourne le payload complet pour /api/edl/save
  // { zone: { dataUrl, originalDataUrl } }
  // ✅ Copie profonde pour éviter mutation du store depuis le backend
  getPhotosPayload() {
    const payload = {}
    for (const [slotId, photo] of Object.entries(_store.photos)) {
      payload[slotId] = clonePhoto(photo)
    }
    return payload
  },

  // ── Utils ────────────────────────────────────────────────────────────────────

  // ✅ Log le nombre de photos supprimées pour faciliter le débogage
  clear() {
    const photosCount  = Object.keys(_store.photos).length
    const departCount  = Object.keys(_store.departPhotos).length
    const prevKey      = _store._activeKey
    _store.photos       = {}
    _store.departPhotos = {}
    _store._activeKey   = null
    console.log(`[photoMemory] cleared — ${photosCount} photos + ${departCount} départ supprimées | was: ${prevKey || 'none'}`)
  },

  // Réinitialise uniquement les photos de la session courante (pas les départ)
  clearCurrentPhotos() {
    const count = Object.keys(_store.photos).length
    _store.photos = {}
    console.log(`[photoMemory] clearCurrentPhotos — ${count} photos supprimées`)
  },

  // Injecte les photos dans un objet inspection (pour generatePDF, upload...)
  // ✅ Guard : inspection peut être null/undefined
  injectInto(inspection) {
    const base = inspection && typeof inspection === 'object' ? inspection : {}
    return {
      ...base,
      photos:       this.getPhotos(),
      departPhotos: this.getDepartPhotos(),
    }
  },

  // ── Debug ────────────────────────────────────────────────────────────────────

  // Retourne un résumé lisible de l'état du store (pour console dev)
  debug() {
    const photos  = Object.entries(_store.photos).map(([id, p]) => ({
      slotId:    id,
      annoteeKo: Math.round((p.dataUrl?.length ?? 0) * 0.75 / 1024),
      origKo:    Math.round((p.originalDataUrl?.length ?? 0) * 0.75 / 1024),
    }))
    const depart  = Object.keys(_store.departPhotos)
    console.table(photos)
    console.log(`[photoMemory] activeKey: ${_store._activeKey || 'none'}`)
    console.log(`[photoMemory] departPhotos: [${depart.join(', ')}]`)
    return { photos, departSlots: depart, activeKey: _store._activeKey }
  },
}